<template>
  <div class="ficha">
    <div v-if="entrada.carregando" class="caderno__ficha-vazia">
      {{ $t('inventory.enciclopedia.carregando') }}
    </div>

    <article v-else-if="entrada.dados" class="enciclopedia">
      <header class="enciclopedia__cabecalho">
        <img
          v-if="entrada.dados.arte"
          class="enciclopedia__arte"
          :src="entrada.dados.arte"
          :alt="entrada.dados.nomeExibido"
        />
        <h2 class="enciclopedia__nome">{{ entrada.dados.nomeExibido }}</h2>
      </header>

      <p v-if="entrada.dados.descricao" class="enciclopedia__descricao">{{ entrada.dados.descricao }}</p>

      <section class="enciclopedia__bloco">
        <h3 class="enciclopedia__bloco-titulo">{{ $t('inventory.enciclopedia.noJogo') }}</h3>
        <dl class="enciclopedia__linhas">
          <div><dt>{{ $t('fishCollection.stats.rarity') }}</dt><dd>{{ $t(`rarities.${entrada.dados.noJogo.raridade}`) }}</dd></div>
          <div><dt>{{ $t('inventory.enciclopedia.pesoBase') }}</dt><dd class="t-numero">{{ entrada.dados.noJogo.pesoBase }} kg</dd></div>
          <div><dt>{{ $t('inventory.enciclopedia.valor') }}</dt><dd class="t-numero">{{ formatoDeOuro(entrada.dados.noJogo.valor) }}</dd></div>
          <div class="enciclopedia__linha--larga">
            <dt>{{ $t('inventory.enciclopedia.ondeAparece') }}</dt>
            <dd>
              <!-- 8.7: a ficha leva à carta. O mapa que o jogador ainda não liberou continua
                   listado — saber onde procurar é o ponto do caderno. -->
              <button
                v-for="mapa in entrada.dados.noJogo.mapas"
                :key="mapa.id"
                type="button"
                class="enciclopedia__mapa"
                @click="$emit('ir-para-o-mapa', mapa)"
              >{{ nomeDoMapa(mapa.nome) }}</button>
              <span v-if="!entrada.dados.noJogo.mapas.length">—</span>
            </dd>
          </div>
        </dl>
      </section>

      <section v-if="entrada.dados.naVidaReal" class="enciclopedia__bloco">
        <h3 class="enciclopedia__bloco-titulo">{{ $t('inventory.enciclopedia.naVidaReal') }}</h3>
        <dl class="enciclopedia__linhas">
          <div class="enciclopedia__linha--larga">
            <dt>{{ $t('inventory.enciclopedia.nomeCientifico') }}</dt>
            <dd><em>{{ entrada.dados.naVidaReal.nomeCientifico }}</em></dd>
          </div>
          <div v-if="entrada.dados.naVidaReal.habitat" class="enciclopedia__linha--larga">
            <dt>{{ $t('inventory.enciclopedia.habitatReal') }}</dt>
            <dd>{{ entrada.dados.naVidaReal.habitat }}</dd>
          </div>
          <div v-if="entrada.dados.naVidaReal.comprimentoMaximoCm">
            <dt>{{ $t('inventory.enciclopedia.comprimento') }}</dt>
            <dd class="t-numero">{{ entrada.dados.naVidaReal.comprimentoMaximoCm }} cm</dd>
          </div>
          <div v-if="entrada.dados.naVidaReal.pesoMaximoKg">
            <dt>{{ $t('inventory.enciclopedia.pesoReal') }}</dt>
            <dd class="t-numero">{{ entrada.dados.naVidaReal.pesoMaximoKg }} kg</dd>
          </div>
          <div v-if="entrada.dados.naVidaReal.conservacao" class="enciclopedia__linha--larga">
            <dt>{{ $t('inventory.enciclopedia.conservacao') }}</dt>
            <dd>{{ $t(`inventory.enciclopedia.iucn.${entrada.dados.naVidaReal.conservacao}`) }}</dd>
          </div>
        </dl>
      </section>

      <section class="enciclopedia__bloco">
        <h3 class="enciclopedia__bloco-titulo">{{ $t('inventory.enciclopedia.seu') }}</h3>
        <dl class="enciclopedia__linhas">
          <div><dt>{{ $t('inventory.enciclopedia.quantosPegou') }}</dt><dd class="t-numero">{{ entrada.dados.seu.quantosPegou }}</dd></div>
          <div>
            <dt>{{ $t('inventory.enciclopedia.maior') }}</dt>
            <dd class="t-numero">{{ entrada.dados.seu.maiorPeso.toFixed(2) }} kg</dd>
          </div>
          <div v-if="entrada.dados.seu.maiorPesoMapa" class="enciclopedia__linha--larga">
            <dt>{{ $t('inventory.enciclopedia.ondePegou') }}</dt>
            <dd>{{ nomeDoMapa(entrada.dados.seu.maiorPesoMapa) }}</dd>
          </div>
          <div><dt>{{ $t('inventory.enciclopedia.primeira') }}</dt><dd>{{ emData(entrada.dados.seu.primeiraCaptura) }}</dd></div>
          <div><dt>{{ $t('inventory.enciclopedia.ultima') }}</dt><dd>{{ emData(entrada.dados.seu.ultimaCaptura) }}</dd></div>
        </dl>
      </section>

      <p v-if="entrada.dados.curiosidade" class="enciclopedia__curiosidade">
        <span class="enciclopedia__curiosidade-rotulo">{{ $t('inventory.enciclopedia.curiosidade') }}</span>
        {{ entrada.dados.curiosidade }}
      </p>
    </article>
  </div>
</template>

<script>
import { getMapKey } from '@/shared/utils/mapNames';

/**
 * A FICHA DO PEIXE — a página da direita do caderno (D14, D3).
 *
 * Foto, nome e a tabela em três blocos: no jogo, na vida real, e o seu. O bloco real some para
 * peixe inventado ou mítico, e o leiaute é o MESMO — quem não existe de verdade traz só dado do
 * jogo, sem uma segunda tela para isso.
 *
 * Vive num arquivo próprio porque a ficha aparece em DOIS lugares: a página da direita no desktop
 * e a página inteira no telefone, que não tem dupla. Duplicar oitenta linhas de tabela entre os
 * dois é exatamente como um rótulo acabaria certo num e errado no outro.
 *
 * **Burro de propósito**: recebe `entrada` pronta e não busca nada. Quem sabe qual peixe está
 * aberto é a tela — que também é quem sabe o que fazer com o 404 de peixe não descoberto.
 */
export default {
  name: 'FichaDoPeixe',
  props: {
    /** `{ aberta, carregando, dados }` — a mesma forma que a tela guarda. */
    entrada: { type: Object, required: true },
  },
  emits: ['ir-para-o-mapa'],
  methods: {
    formatoDeOuro(valor) {
      return new Intl.NumberFormat('pt-BR').format(valor || 0);
    },

    /** O `maps.name` do banco para o nome traduzido. A tabela mora em `utils/mapNames`. */
    nomeDoMapa(nome) {
      const chave = `maps.mapNames.${getMapKey(nome)}`;
      const traduzido = this.$t(chave);
      return traduzido === chave ? nome : traduzido;
    },

    emData(iso) {
      return iso ? new Date(iso).toLocaleDateString(this.$i18n.locale) : '—';
    },
  },
};
</script>

<style scoped>
/* A ficha ocupa a página inteira e rola dentro dela: o papel tem altura fixa (a proporção do
 * recorte manda), e peixe com descrição longa não pode empurrar o rodapé para fora da arte. */
.ficha {
  height: 100%;
  overflow-y: auto;
}

.enciclopedia {
  display: flex;
  flex-direction: column;
  gap: var(--e-3);
}

/* Arte e nome lado a lado: o nome é o título da página da direita, e pôr um sobre o outro
 * gastaria altura que a tabela quer. */
.enciclopedia__cabecalho {
  display: flex;
  align-items: center;
  gap: var(--e-3);
  border-bottom: 2px dashed var(--papel-sombra);
  padding-bottom: var(--e-2);
}

.enciclopedia__arte {
  width: 96px;
  height: 72px;
  object-fit: contain;
  flex: 0 0 auto;
  image-rendering: auto;
}

.enciclopedia__nome {
  margin: 0;
  font-family: var(--fonte-display);
  font-size: var(--f-3);
  color: var(--tinta);
}

@media (min-width: 1280px) {
  .enciclopedia__arte {
    width: 140px;
    height: 105px;
  }
}

/* A descrição é a voz do Velho João — texto corrido, e é ele que se lê primeiro. */
.enciclopedia__descricao {
  margin: 0;
  font-family: var(--fonte-corpo);
  font-size: var(--f-2);
  line-height: 1.5;
  color: var(--tinta);
}

.enciclopedia__bloco-titulo {
  margin: 0 0 var(--e-1);
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  color: var(--tinta-fraca);
  border-bottom: 2px dashed var(--papel-sombra);
  padding-bottom: var(--e-1);
}

/* Duas colunas de fichinha; a linha larga ocupa as duas quando o valor é texto e não número. */
.enciclopedia__linhas {
  margin: 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--e-1) var(--e-3);
}

.enciclopedia__linhas > div {
  display: flex;
  flex-direction: column;
}

.enciclopedia__linha--larga {
  grid-column: 1 / -1;
}

.enciclopedia__linhas dt {
  font-size: var(--f-1);
  color: var(--tinta-fraca);
}

.enciclopedia__linhas dd {
  margin: 0;
  font-size: var(--f-2);
  color: var(--tinta);
}

/* O mapa é um link para a carta (8.7): sublinhado a lápis, sem cara de botão. */
.enciclopedia__mapa {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: var(--latao);
  text-decoration: underline dashed;
  cursor: pointer;
}

.enciclopedia__mapa + .enciclopedia__mapa::before {
  content: ' · ';
  color: var(--tinta-fraca);
  text-decoration: none;
}

/* A curiosidade fecha a folha, recuada como nota de rodapé de caderno de campo. */
.enciclopedia__curiosidade {
  margin: 0;
  border-left: 3px solid var(--papel-sombra);
  padding-left: var(--e-2);
  font-family: var(--fonte-corpo);
  font-size: var(--f-2);
  line-height: 1.5;
  color: var(--tinta-fraca);
}

.enciclopedia__curiosidade-rotulo {
  display: block;
  font-family: var(--fonte-display);
  font-size: var(--f-1);
  color: var(--latao);
}
</style>
